import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import { ThemeColorPalette } from './theme-color-palette.interface';

export function createThemePreset(colors: ThemeColorPalette) {
  return definePreset(Aura, {
    semantic: {
      primary: colors.primary,
      contrast: colors.contrast,
      neutral: colors.neutral,
      success: colors.success,
      warning: colors.warning,
      danger: colors.danger,
    },
    components: {
      accordion: {
        css: ({ dt }: any) => `
          .tar-accordion .p-accordionpanel {
            margin: 0.5rem 0;
            border-radius: 1rem;
            border: 1px solid var(--p-neutral-200);
          }

          .tar-accordion .p-accordionpanel > .p-accordionheader {
            background-color: var(--p-neutral-50) ;
            border-radius: 1rem ;
          }

          .tar-accordion .p-accordionpanel:not(.p-accordionpanel-active):not(.p-disabled) > .p-accordionheader:hover,
          .tar-accordion .p-accordionpanel:not(.p-disabled).p-accordionpanel-active > .p-accordionheader{
            background-color: var(--p-neutral-50) ;
            border-radius: 1rem ;
          }

          .tar-accordion .p-accordionpanel.p-accordionpanel-active{
            background-color: var(--p-neutral-50)
          }

          .tar-accordion .p-accordioncontent,
          .tar-accordion .p-accordioncontent .p-accordioncontent-content {
            border-radius: 1rem;
            padding: 0 0 0 0 ;
            background-color: var(--p-neutral-50);
          }

          /* Safari: the projected content components are custom elements with the
             default display:inline. After the collapse -> re-expand animation Safari
             fails to re-propagate the height of block content inside an inline host,
             leaving the panel collapsed to its 2px padding until an unrelated reflow.
             Block-level hosts make Safari invalidate the ancestor height correctly. */
          .tar-accordion .p-accordioncontent-content > * {
            display: block;
          }

          /* Safari/WebKit: @angular/animations can leave a stale inline height/visibility
             on the animated content div after collapse -> re-expand (zoneless + async
             animations + height:'*'). Once the panel is active and not animating, force
             natural height. Remove when PrimeNG drops @angular/animations here. */
          .tar-accordion .p-accordionpanel-active > .p-accordioncontent > .p-accordioncontent-content:not(.ng-animating) {
            height: auto !important;
            visibility: visible !important;
          }

          .tar-accordion .p-accordionpanel .p-ripple.p-accordionheader > .p-component.p-iconwrapper{
            transform: rotate(180deg);
          }

          .tar-accordion .p-accordionpanel:has(.p-accordionheader[aria-expanded="false"]) .when-open {
            display: none !important;
          }

          .white-accordion .tar-accordion .p-accordionpanel > .p-accordionheader,
          .white-accordion .tar-accordion .p-accordionpanel.p-accordionpanel-active,
          .white-accordion .tar-accordion .p-accordioncontent,
          .white-accordion .tar-accordion .p-accordioncontent .p-accordioncontent-content,
          .white-accordion .tar-accordion .p-accordionpanel:not(.p-accordionpanel-active):not(.p-disabled) > .p-accordionheader:hover,
          .white-accordion .tar-accordion .p-accordionpanel:not(.p-disabled).p-accordionpanel-active > .p-accordionheader {
            background-color: white;
          }

          .small-aligned-accordion > .tar-accordion {
            >.p-accordionpanel {
              margin: 0;
              border-radius: 0.5rem;

              &:not(.p-accordionpanel-active) {
                border: unset;
              }

              &.p-accordionpanel-active {
                background-color: white;

                .p-accordionheader, .p-accordioncontent-content, .p-accordioncontent {
                  background-color: white;
                }
              }

              >.p-accordionheader {
                padding: 0.5rem;
              }

              >.p-accordioncontent {
                padding: 0rem 0.5rem;
              }
            }
          }
        `,
      },
      select: {
        root: {
          paddingX: '1.25rem',
          paddingY: '0.875rem',
          color: colors.primary['500'],
          background: 'white',
          placeholderColor: colors.primary['500'],
          borderColor: colors.primary['500'],
          borderRadius: '1rem',
          hoverBorderColor: colors.primary['500'],
        },
        list: {
          padding: '0.5rem 1.2rem',
        },
        option: {
          padding: '0',
          selectedBackground: colors.primary['50'],
        },
        dropdown: {
          color: colors.primary['500'],
        },
        css: ({ dt }: any) => `
            .p-select {
              border:2px solid ${dt('semantic.primary.500')};
              font-size: 1rem;
              font-weight: 600;

              &.long-programme-start-point-select {
               .p-select-option {
                  line-height: 1.3;
                }
              }
            }
            .p-select-option.p-select-option-selected{
              border: 2px solid ${dt('semantic.primary.200')};
            }
            .p-select .p-select-overlay{
              border-radius: 1rem;
            }
            .test-select .p-select {
              width: 100%;
            }
            .select-black.p-select,
            .select-black.p-select:not(.p-disabled).p-focus,
            .select-black.p-select:not(.p-disabled):hover {
              border: 1px solid ${dt('semantic.neutral.900')};
            }
            .select-black.p-select .p-select-dropdown {
              color: ${dt('semantic.neutral.900')};
            }

            .programme-filter {
              &.p-select {
                border: 0;
                background: transparent;
                box-shadow: unset;
                flex-direction: row-reverse;
              }

              .p-select-label, .p-select-dropdown {
                color: black;
                padding: 0;
                line-height: 1.5rem;
                font-weight: 600;
              }

              .p-select-list {
                padding: 0.5rem 0.6rem;

                .p-select-option {
                  padding: 0 0.6rem;
                }
              }
            }
        `,
      },
      selectbutton: {
        css: ({ dt }: any) => `
          .tar-selectbutton.p-selectbutton {
            border: 2px solid var(--p-neutral-200) ;
            border-radius: 1rem;
          }
          .tar-selectbutton .p-togglebutton {
            padding: 0.875rem 1.5rem;
            background-color: transparent;
            border: 0;
          }
          .tar-selectbutton .p-togglebutton span{
            color: var(--p-neutral-500);
          }
          .tar-selectbutton .p-togglebutton-checked span{
            color: var(--p-primary-500);
          }
          .tar-selectbutton .p-togglebutton-checked.p-togglebutton:before{
            border:2px solid var(--p-primary-500);
            border-radius: 0.8rem;
          }
          .tar-selectbutton .p-ripple.p-togglebutton.p-component.p-togglebutton-checked,
          .tar-selectbutton .p-togglebutton:not(:disabled):not(.p-togglebutton-checked):hover{
            background-color: transparent;
          }

          .tar-selectbutton.small.p-selectbutton {
            border-width: 1px;
            width: fit-content;
            padding: 0.25rem;

            .p-togglebutton {
              min-width: 4.5rem;
              padding: 0.25rem;
              height: 2.25rem;

              span {
                font-size: 1rem;
                line-height: 1rem;
              }

              &.p-togglebutton-checked {
                border: 2px solid var(--p-primary-500);
                border-radius: 0.75rem;
              }
            }
          }
        `,
      },
      breadcrumb: {
        root: {
          padding: '0 1rem 0',
          gap: '1.5rem',
        },
        item: {
          color: colors.primary['500'],
          hoverColor: colors.primary['700'],
        },
        css: ({ dt }: any) => `
        .p-breadcrumb-item-label {
          font-size: 1.125rem;
          font-weight: 400;
          line-height: 1;
          text-decoration: underline;
        }
      `,
      },
      button: {
        root: {
          paddingX: '1.25rem',
          paddingY: '0.875rem',
          iconOnlyWidth: '2.75rem',
          label: {
            fontWeight: '600',
          },
          roundedBorderRadius: '1rem',
        },
        css: ({ dt }: any) => `
        .p-button:not(.p-button-text) {
          height: 2.75rem;
          font-size: 1rem;
          line-height: 1;
          font-weight: 600;
          box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1),
            0px 2px 4px -2px rgba(0, 0, 0, 0.1);
        }

        .p-button-text {
          border: unset !important;
          padding: 0.375rem 1.25rem 0.375rem 1.25rem;
          height: fit-content;

          .p-button-icon {
              line-height: 1.25rem;
          }
        }

        .p-button-contrast {
          border-color: ${dt('semantic.contrast.500')};
          background-color: ${dt('semantic.contrast.500')};
        }
        .p-button-contrast:not(:disabled):hover {
          border-color: ${dt('semantic.contrast.600')};
          background-color: ${dt('semantic.contrast.600')};
        }
        .p-button-contrast:not(:disabled):active {
          border-color: ${dt('semantic.contrast.700')};
          background-color: ${dt('semantic.contrast.700')};
        }
        .p-button-outlined {
          border-width: 2px;
          border-color: ${dt('semantic.primary.500')};
          color: ${dt('semantic.primary.500')};
          background-color: #fff;
          box-shadow: none;
        }

        .p-button-outlined:not(:disabled):hover {
          border-width: 2px;
          border-color: ${dt('semantic.primary.600')};
          color: ${dt('semantic.primary.600')};
          background-color: ${dt('semantic.primary.100')};
        }

        .p-button-outlined:not(:disabled):active {
          border-width: 2px;
          border-color: ${dt('semantic.primary.600')};
          color: ${dt('semantic.primary.600')};
          background-color: ${dt('semantic.primary.200')};
        }

        .p-button:disabled {
          background: ${dt('semantic.neutral.100')};
          border: 1px solid ${dt('semantic.neutral.100')};
          color: ${dt('semantic.neutral.500')};
          opacity: 1;
          box-shadow: none;
        }

        .p-button-icon {
          font-size: 1.25rem;
        }

        .p-button-icon-only {
          box-shadow: none
        }

        .p-button-icon-only.p-button-rounded {
          border-radius: 1rem;
          padding: 0.6875rem;
        }
        .p-button.borderless,
        .p-button.borderless:hover,
        .p-button.borderless:focus {
          background-color: inherit;
          border: 0;
          box-shadow: none;
        }

        .tar-btn.p-button { position: relative; }

        .tar-btn.is-loading .p-button-icon,
        .tar-btn.is-loading .p-button-icon-left,
        .tar-btn.is-loading .p-button-icon-right,
        .tar-btn.is-loading .p-button-label {
          visibility: hidden;
        }

        .tar-btn.is-loading { cursor: progress; }

        .tar-btn .loading-overlay .p-progress-spinner-circle {
          stroke: ${dt('semantic.primary.500')};
        }

        .icon-4.p-button {

          &.p-button-icon-only {
            width: 1rem;
          }

         .p-button-icon {
            line-height: 1rem;
            font-size: 1rem;
          }
        }

        .icon-black.p-button:not(:disabled) {
          color: black;
        }

        .square-button.p-button {
          width: 2rem;
          height: 2rem;
          border-radius: 6px;

          &:not(:disabled) {
            color: white;
            background-color: var(--p-primary-500);
          }
        }

        .reverse-color.p-button {
          background-color: white;
          color: var(--p-primary-500);
        }

        .p-button.padingless,
        .p-button.padingless:hover,
        .p-button.padingless:focus {
          padding: 0;
        }

        .tar-dialog-danger-primary.p-button {
          background: var(--p-danger-600);
          border-color: var(--p-danger-600);
        }

        .tar-dialog-danger-primary.p-button:not(:disabled):hover {
          background: var(--p-danger-700);
          border-color: var(--p-danger-700);
        }

        .tar-dialog-danger-secondary.p-button {
          color: var(--p-danger-600);
          border-color: var(--p-danger-600);
        }

        .tar-dialog-danger-secondary.p-button:not(:disabled):hover {
          color: var(--p-danger-700);
          border-color: var(--p-danger-700);
        }

        .tar-dialog-danger-text.p-button {
          color: var(--p-danger-600);
          border-color: transparent;
        }

        .tar-dialog-danger-text.p-button:not(:disabled):hover {
          color: var(--p-danger-700);
          background: transparent;
        }

        .p-button.p-datepicker-prev-button.p-button-icon-only,
        .p-button.p-datepicker-next-button.p-button-icon-only{
          height: 1.5rem;
          width: 1.5rem;
          padding: 0;
        }

        .advice-button {
          background-color: white;
          border-radius: 0.25rem;
          padding: 5px 1rem;
          height: auto !important;
          width: 100%;

          .p-button-label {
            font-size: 16px;
            line-height: 1;
            text-box: trim-both cap alphabetic;
          }
          .p-button-icon {
            font-size: 16px;
            line-height: 0;
          }

          &.urgent {
            color: var(--p-danger-700);
            border-color: var(--p-danger-700);

            &:hover {
              background-color: var(--p-danger-50);
              color: var(--p-danger-700);
              border-color: var(--p-danger-700);
            }
          }

          &.alert {
            color: var(--p-warning-700);
            border-color: var(--p-warning-700);

            &:hover {
              background-color: var(--p-warning-50);
              color: var(--p-warning-700);
              border-color: var(--p-warning-700);
            }
          }

          &.developing {
            color: var(--p-success-700);
            border-color: var(--p-success-700);

            &:hover {
              background-color: var(--p-success-50);
              color: var(--p-success-700);
              border-color: var(--p-success-700);
            }
          }

          &.encouraging {
            color: var(--p-primary-700);
            border-color: var(--p-primary-700);

            &:hover {
              background-color: var(--p-primary-50);
              color: var(--p-primary-700);
              border-color: var(--p-primary-700);
            }
          }
        }

        .rekensprint-mathwall-results-download.is-loading {
          .loading-overlay {
            display: inline-flex;
            justify-content: center;
            align-items: center;
            gap: 0.5rem;

            .load-label {
              text-box: trim-both cap alphabetic;
            }
          }

          p-button-icon,
          .p-button-icon-left,
          .p-button-icon-right,
          .p-button-label {
            display: none;
          }
        }
      `,
      },
      dialog: {
        root: {
          borderRadius: '1rem',
        },
        header: {
          padding: '0',
        },
        content: {
          padding: '0',
        },
        footer: {
          padding: '0',
        },
        css: ({ dt }: any) => `
        .p-overlay-mask {
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(24px);
        }

        .p-dialog {
          padding: 2.5rem;
          min-width: 38.9%;
          max-width: 46.75rem;
          border: none;
          box-shadow: 0 4px 6px -1px #0000001A, 0 2px 4px -2px #0000001A;
        }

        .p-dialog-header-actions {
          margin-bottom: 1rem;

          .p-dialog-close-button {
            height: 2rem;
            width: 2rem;
            padding: 0;

            i {
              font-size: 1.5rem;
              color: #000;
            }
          }
        }

        .tar-popup {
          &.p-dialog {
            padding: 1rem;
            border: 1px solid var(--p-neutral-200);
            min-width: 0;
            min-height: 0;

            .p-dialog-content {
              scrollbar-width: none;
              -ms-overflow-style: none;

              &::-webkit-scrollbar {
                  display: none;
              }
            }


          }
        }

        .alternative .tar-popup.p-dialog {
          padding: 2rem;
        }
      `,
      },
      datepicker: {
        panel: {
          borderRadius: '1rem',
          padding: '0.75rem 0.125rem',
        },
        dropdown: {
          width: '3rem',
          borderRadius: '1rem',
          borderColor: colors.neutral['300'],
          hoverBorderColor: colors.neutral['500'],
          activeBorderColor: colors.neutral['900'],
        },
        css: ({ dt }: any) => `
        button.p-datepicker-dropdown {
          background-color: ${dt('semantic.primary.50')};
          font-size: 1.5rem;
          color: ${dt('semantic.neutral.900')};
        }
        button.p-datepicker-dropdown:not(:disabled):hover {
          background-color: ${dt('semantic.primary.50')};
        }
        .p-inputtext.p-datepicker-input {
          padding: 0.75rem 1rem;
          border-radius: 1rem;
          border: 1px solid ${dt('semantic.neutral.300')};
          color: ${dt('semantic.neutral.500')};
          font-weight: 500;
          font-size: 1rem;
          line-height: 1rem;
          max-height: 2.75rem;
        }
        .p-inputtext.p-datepicker-input::placeholder {
          color: ${dt('semantic.neutral.500')};
        }
        .p-inputtext.p-datepicker-input:enabled:hover {
          border-color: ${dt('semantic.neutral.500')};
        }
        .p-inputtext.p-datepicker-input:enabled:active {
          color: #000;
        }
        .p-inputtext.p-datepicker-input:enabled:hover ~ .p-datepicker-dropdown {
          border-color: ${dt('semantic.neutral.500')};
          background-color: ${dt('semantic.primary.50')};
        }
        .p-datepicker.p-component.p-inputwrapper:hover {
          .p-datepicker-input:enabled {
            border-color: ${dt('semantic.neutral.500')};
          }
        }
        .p-inputtext.p-datepicker-input:enabled:focus {
          border-color: ${dt('semantic.neutral.900')};
        }
        .p-inputtext.p-datepicker-input:enabled:focus ~ .p-datepicker-dropdown {
          border-color: ${dt('semantic.neutral.900')};
          background-color: ${dt('semantic.primary.50')};
        }
        .p-inputtext.p-datepicker-input:disabled {
          border-color: ${dt('semantic.neutral.200')};
          background: ${dt('semantic.neutral.100')};
          color: ${dt('semantic.neutral.500')};
        }
        .p-inputtext.p-datepicker-input:disabled ~ .p-datepicker-dropdown {
          border-color: ${dt('semantic.neutral.200')};
          background: ${dt('semantic.neutral.100')};
          color: ${dt('semantic.neutral.400')};
          cursor: default;
        }
        div.p-datepicker-header {
          padding: 0 1.5rem 0.75rem;
          border-bottom: 1px solid ${dt('semantic.primary.100')};
        }
        div.p-datepicker-title,
        .p-ripple.p-datepicker-select-year,
        .p-ripple.p-datepicker-select-month {
          font-weight: 400;
          font-size: 1rem;
          line-height: 1;
          color: ${dt('semantic.primary.500')};
        }
        .p-ripple.p-datepicker-day,
        .p-datepicker-year,
        .p-datepicker-month {
          font-weight: 400;
          font-size: 1rem;
          line-height: 1;
          color: #000;
        }
        .p-ripple.p-datepicker-day.p-disabled {
          opacity: 1;
          color: ${dt('semantic.neutral.500')};
        }
        .p-ripple.p-datepicker-select-year:enabled:hover,
        .p-ripple.p-datepicker-select-month:enabled:hover,
        .p-ripple.p-datepicker-day.p-datepicker-day-selected,
        .p-ripple.p-datepicker-year.p-datepicker-year-selected,
        .p-ripple.p-datepicker-month.p-datepicker-month-selected,
        .p-ripple.p-datepicker-year:not(.p-disabled):not(.p-datepicker-year-selected):hover,
        .p-ripple.p-datepicker-month:not(.p-disabled):not(.p-datepicker-month-selected):hover,
        .p-ripple.p-datepicker-day:not(.p-disabled):not(.p-datepicker-day-selected):hover {
          color: ${dt('semantic.primary.600')};
          background-color: ${dt('semantic.primary.50')};
        }
        .p-datepicker-today > .p-datepicker-day:not(.p-datepicker-day-selected){
          background: unset;
        }
        .p-datepicker-today > .p-datepicker-day:not(.p-datepicker-day-selected):not(.p-disabled) {
          color: #000;
        }
        .p-datepicker-weekday {
          font-weight: 600;
          font-size: 1rem;
          line-height: 1;
          color: #000;
        }
        .p-datepicker-day-view,
        .p-datepicker-month-view,
        .p-datepicker-year-view {
          margin: 0.75rem 0;
        }
        .p-datepicker-buttonbar {
          padding: 0.75rem 1.5rem 0;
          border-top: 1px solid ${dt('semantic.primary.100')};
        }
        .p-button.p-datepicker-today-button,
        .p-button.p-datepicker-clear-button {
          border: none;
          color: ${dt('semantic.primary.500')};
          background-color: transparent;
          box-shadow: none;
        }
        .p-button.p-datepicker-today-button:not(:disabled):hover,
        .p-button.p-datepicker-clear-button:not(:disabled):hover {
          border: none;
          color: ${dt('semantic.primary.600')};
          background-color: transparent;
          box-shadow: none;
        }
        .p-button.p-datepicker-today-button:not(:disabled):active,
        .p-button.p-datepicker-clear-button:not(:disabled):active {
          border: none;
          color: ${dt('semantic.primary.700')};
          background-color: transparent;
          box-shadow: none;
        }
        .p-button.p-datepicker-today-button:disabled,
        .p-button.p-datepicker-clear-button:disabled {
          border: none;
          color: ${dt('semantic.neutral.400')};
          background-color: transparent;
          box-shadow: none;
          opacity: 1;
        }

        .short .p-datepicker-input {
          width: 7.5rem;
        }
        `,
      },
      tabs: {
        tablist: {
          borderColor: colors.neutral['300'],
          background: 'transparent',
        },
        tab: {
          fontWeight: '500',
          padding: '0.5rem 1.5rem',
          color: colors.neutral['900'],
          hoverColor: colors.neutral['900'],
          activeColor: colors.primary['500'],
          borderColor: colors.neutral['300'],
          hoverBorderColor: colors.neutral['300'],
        },
        tabpanel: {
          padding: '0.625rem 0 0',
          background: 'transparent',
        },
        activeBar: {
          background: colors.primary['500'],
        },
        css: ({ dt }: any) => `
        .p-tab {
          display: flex;
          align-items: center;
        }
        .p-tab.p-tab-active {
          font-weight: 600;
        }
        .p-tablist-content {
          flex-grow: 0;
        }
        .bareka-results-tabs .p-tabpanels {
          padding: 0;
          background: transparent;
        }
      `,
      },
      dropdown: {
        //   css: ({ dt }: any) => `
        //   .tar-dropdown.p-component.p-dropdown.p-select .p-select-label {
        //       padding: 0.625rem;
        //   }
        // `,
      },
      radioButton: {
        checked: {
          background: colors.primary['500'],
        },
      },
      card: {
        root: {
          color: '#000',
          background: colors.neutral['50'],
          shadow: 'none',
        },
        body: {
          padding: '1rem',
          gap: '0.75rem',
        },
        title: {
          fontSize: '1rem',
          fontWeight: '600',
        },
        css: ({ dt }: any) => `
          .p-card {
            border-radius: 1rem;
            border: 1px solid ${dt('semantic.neutral.200')};
          }

          .no-side-paddings .p-card-body {
            padding-left: 0;
            padding-right: 0;
          }
        `,
      },
      popover: {
        root: {
          arrowOffset: '0',
          borderRadius: '1rem',
          borderColor: 'transparent',
          gutter: '0',
        },
        content: {
          padding: '1rem',
        },
        css: ({ dt }: any) => `
          .atr-advice-popover {
            border: 1px solid var(--p-neutral-200);
            transform: translateY(-50%) !important;

            &::after {
              border: unset;
            }

            &::before {
              top: calc(50% - 1.125rem);
              left: -1.5rem;
              border-top: 1.125rem solid transparent;
              border-bottom: 1.125rem solid transparent;
              border-right: 1.75rem solid white;
              border-top-right-radius: 4px;
              border-bottom-right-radius: 4px;
              filter: drop-shadow(-2px 0 2px rgba(0, 0, 0, 0.15));
            }

            &.atr-advice-popover__reverse {
              &::before {
                left: unset;
                right: -1.5rem;
                border-right: unset;
                border-left: 1.75rem solid white;

                border-top-right-radius: unset;
                border-bottom-right-radius: unset;
                border-top-left-radius: 4px;
                border-bottom-left-radius: 4px;

                filter: drop-shadow(2px 0 2px rgba(0,0,0,0.15));
              }
            }

            .p-button-label {
              text-box: trim-both cap alphabetic;
            }
          }

          .brick-popover {
            margin-left: 2.5rem;
            width: min(34rem, calc(100vw - 2rem));

            .p-popover-content {
              padding: 0.75rem;
            }

            .overview-popover {
              max-height: min(22rem, 60vh);
              overflow-y: auto;
              padding-right: 0.25rem;
            }

            .overview-popover ul {
              display: flex;
              flex-wrap: wrap;
              gap: 0.25rem;
              margin: 0;
              padding: 0;
              list-style: none;
            }

            .overview-popover li {
              display: flex;
              min-width: 0;
            }

            app-brick-card {
              max-width: 8.5rem;
            }
          }
        `,
      },
      tooltip: {
        root: {
          background: colors.primary['800'],
          borderRadius: '0.5rem',
          maxWidth: '20rem',
          padding: '0.25rem 0.5rem',
          shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
        },
        css: ({ dt }: any) => `
          .p-tooltip-text {
            font-size: 16px;
            font-weight: 500;
            line-height: 16px;
          }
        `,
      },
      datatable: {
        css: ({ dt }: any) => `
          .p-datatable-table {
            table-layout: fixed;
          }

          .p-datatable-tbody tr:nth-child(odd) {
            background-color: var(--p-neutral-50);
            border-radius: 0.5rem;
          }

          .planning-table .p-datatable-table .p-datatable-tbody tr:nth-child(odd){
            background-color: inherit;
          }

          .planning-table .p-datatable-table .p-datatable-thead>tr>th{
            text-align: left;
          }

          .planning-table .p-datatable-table .p-datatable-tbody>tr>td:not(:first-child),
          .p-datatable-thead>tr>th:not(:first-child) {
            text-align: left;

            .cell-container {
                justify-content: left;
            }
          }

          .p-datatable-tbody>tr>td:not(:first-child),
          .p-datatable-thead>tr>th:not(:first-child) {
            text-align: center;

            .cell-container {
                justify-content: center;
            }
          }

          .p-datatable-tbody>tr>td {
            padding: 0.25rem 0.5rem 0.25rem 0.5rem;
            font-size: 1rem;
            font-weight: 500;
            line-height: 1rem;
            color: var(--p-neutral-900);
            border-width: 0;
            height: 2.25rem;
          }

          .p-datatable-thead>tr>th {
            padding: 0.25rem 0.5rem 0.25rem 0.5rem;
            font-size: 1rem;
            font-weight: 500;
            line-height: 1rem;
            color: var(--p-neutral-600);
            border-width: 0;
            height: 1.75rem;
          }
        `,
      },
      treeselect: {
        css: ({ dt }: any) => `
          .p-tree-node-content {
            min-width: 0;
          }

          .p-tree-node-label {
            display: block;
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .p-treeselect {
            height: 2.75rem;
            border-radius: 1rem;
            border: 1px solid var(--p-neutral-300);

            .p-treeselect-label {
              height: 100%;
              align-items: center;
              line-height: 1rem;
              font-size: 1rem;
              color: var(--p-neutral-400);
            }
          }

          .p-treeselect-overlay {
            padding: 2.5rem;
            display: flex;
            flex-direction: column;
            border-radius: 1rem;
            background-color: white;

            &.user-group-select {
              height: 37.5rem;
              width: 33.75rem;

              .p-tree-empty-message {
                font-style: italic;
                font-weight: 400;
              }
            }

            .search-icon {
              height: 1.375rem;
            }

            .p-tree-node-toggle-button {
              order: 2;
              margin-left: auto;

              .p-icon {
                width: 1.25rem;
                height: 1.25rem;
                color: var(--p-primary-500);
              }
            }

            .p-tree {
              padding: 0;
            }

            .p-treeselect-tree-container {
              max-height: none !important;
              scrollbar-width: none;
              -ms-overflow-style: none;

              &::-webkit-scrollbar {
                  display: none;
              }
            }

            .p-tree-root-children {
              gap: 1rem;

              .p-tree-node:not(.p-tree-node-leaf) {
                border-radius: 1rem;
                border: 1px solid var(--p-neutral-200);
                padding: 1rem;
                background-color: var(--p-neutral-50);
              }

              .user-group-label {
                font-weight: 600;
                font-size: 1rem;
                line-height: 1.5rem;
                display: flex;
                gap: 2.25rem;
                justify-content: space-between;
                align-items: center;

                .selection-count {
                  color: var(--p-primary-500);
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  width: 4rem;
                  height: 1.5rem;
                  padding: 0 0.5rem 0 0.5rem;
                  font-weight: 500;

                  i {
                    font-size: 1.25rem;
                  }
                }
              }

              .p-tree-node-content {
                gap: 0.5rem;
              }

              p-checkbox,
              .p-checkbox-box {
                width: 1rem;
                height: 1rem;
                line-height: 1rem;
              }

              .p-tree-node-children {
                padding-left: 1rem;

                .p-tree-node-content {
                  height: 3.5rem;
                }
              }
            }

            ul[role="tree"] .user-group-label span:not(.selection-count):not(ul[role="group"] *) {
                width: 7.5rem;
                white-space: break-spaces;
            }
          }
        `,
      },
      virtualscroller: {
        css: ({ dt }: any) => `
        .p-virtualscroller {
          scrollbar-width: none;
          -ms-overflow-style: none;

          &::-webkit-scrollbar {
            display: none;
          }
        }

        .p-virtualscroller-content {
          overflow: hidden;
          gap: 1rem;
          display: flex;
          flex-direction: column;
          }
        `,
      },
      menu: {
        list: {
          padding: '0.5rem 1rem 0.5rem 1rem',
          gap: '0.5rem',
        },
        item: {
          color: 'var(--p-primary-500)',
          icon: {
            color: 'var(--p-primary-500)',
          },
        },
        css: ({ dt }: any) => `
        .p-menu {
          min-width: unset;
        }
        .p-menu-item-icon {
          width: 20px;
          height: 20px;
          font-size: 20px;
        }
        .p-menu-item-label {
          font-weight: 600;
          margin-top: 0.125rem;
        }
        `,
      },
      drawer: {
        css: ({ dt }: any) => `
          .p-drawer.p-drawer-open {
            margin: 1rem 1rem 0 0;
            border-radius: 1rem;

             &.bareka-student-drawer,
             &.student-program-result-drawer,
             &.atr-progress-drawer {
              width: min(max(65vw, 40rem), calc(100vw - 2rem));

              @media (max-width: 1680px) {
                width: min(max(80vw, 40rem), calc(100vw - 2rem));
              }

              @media (max-width: 1440px) {
                width: min(max(90vw, 40rem), calc(100vw - 2rem));
              }
            }
          }
        `,
      },
      multiselect: {
        root: {
          hoverBorderColor: 'var(--p-primary-500)',
          focusBorderColor: 'var(--p-primary-500)',
          placeholderColor: 'var(--p-primary-500)',
          borderRadius: '1rem',
        },
        css: ({ dt }: any) => `
        .p-multiselect {
          border: 2px solid var(--p-primary-500)
        }
        .p-multiselect-label-container{
          display: flex;
          align-items: center;
        }

        .schoolyear-multiselect, .brick-multiselect {
          &.p-multiselect {
            border: 0;
            background: transparent;
            box-shadow: unset;
            flex-direction: row-reverse;
          }

          .p-multiselect-label, .p-multiselect-dropdown {
            color: black;
            padding: 0;
            line-height: 20px;
            font-weight: 600;
          }

          .p-multiselect-label {
            line-height: 26px;
          }
        }
        `,
      },
      progressspinner: {
        css: ({ dt }: any) => `
        .p-progressspinner-circle {
          stroke: var(--p-primary-500) !important;
        }
        `,
      },
      toast: {
        css: ({ dt }: any) => `
          .mathwall-results__toast {
            .p-toast-message-success {
              background: var(--p-success-50);
              border-color: var(--p-success-500);
              color: var(--p-success-700);
              border-radius: 1rem;
            }

            .p-toast-message-error {
              background: var(--p-danger-50);
              border-color: var(--p-danger-500);
              color: var(--p-danger-700);
              border-radius: 1rem;
            }

            .p-toast-message-content {
              align-items: center;
              justify-content: space-between;
            }

            .p-toast-close-button {
              width: 1.5rem;
              height: 1.5rem;
            }
          }
        `,
      },
    },
  });
}
